import { useState, useEffect, useCallback, useRef } from 'react';
import { proveedoresService } from '../../services';
import { useDebounce } from '../useDebounce';

/**
 * Hook personalizado para manejar operaciones CRUD de proveedores
 */
export const useProveedores = () => {
  const [proveedores, setProveedores] = useState([]);
  const requestId = useRef(0);
  const fetchAllSuppliers = useCallback(async (name) => {
    const all = [];
    for (let skip = 0; ; skip += 1000) {
      const result = await proveedoresService.getProveedores({ skip, limit: 1000, include_inactive: true, ...(name ? { name } : {}) });
      if (!result.success) throw new Error(result.message || 'Error al consultar proveedores');
      const page = Array.isArray(result.data) ? result.data : [];
      all.push(...page);
      if (page.length < 1000) break;
    }
    return all;
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Aplicar debounce al searchQuery (espera 500ms después de que el usuario deje de escribir)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  /**
   * Transformar datos de la API al formato esperado
   */
  const transformProveedorFromAPI = useCallback((proveedor) => ({
    id: proveedor.id,
    nombre: proveedor.name || '',
    contactoPrincipal: proveedor.primary_contact_name || '',
    telefono: proveedor.primary_contact_phone || proveedor.phone || '',
    email: proveedor.primary_contact_email || proveedor.email || '',
    pais: proveedor.country || '',
    ciudad: proveedor.city || '',
    direccion: proveedor.address || '',
    sitioWeb: proveedor.website || '',
    estado: proveedor.is_active === false ? 'INACTIVE' : (proveedor.status || 'ACTIVE'),
    tiposPago: proveedor.payment_terms || '',
    diasCredito: proveedor.credit_days || 0,
    limiteCredito: proveedor.credit_limit || 0,
    moneda: proveedor.currency || 'USD',
    fechaRegistro: proveedor.created_at || '',
    ultimaCompra: proveedor.last_purchase_date || '',
    totalCompras: proveedor.total_purchases || 0,
    productosActivos: proveedor.active_products_count || 0,
    ordenesCompra: proveedor.purchase_orders_count || 0,
    status: proveedor.is_active ? 'activo' : 'inactivo'
  }), []);

  /**
   * Cargar todos los proveedores
   */
  const loadProveedores = useCallback(async (params = {}) => {
    const request = ++requestId.current;
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await fetchAllSuppliers(params.name);
      if (request === requestId.current) setProveedores(data.map(transformProveedorFromAPI));
    } catch (err) {
      setError('Error de conexión al cargar los proveedores');
      console.error('Error loading proveedores:', err);
      setProveedores([]);
    } finally {
      setIsLoading(false);
    }
  }, [transformProveedorFromAPI, fetchAllSuppliers]);

  /**
   * Buscar proveedores (actualiza query, la búsqueda real se hace con debounce)
   */
  const searchProveedores = useCallback((query) => {
    setSearchQuery(query);
  }, []);
  
  /**
   * Realizar búsqueda real en la API (se ejecuta con debounce)
   */
  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      // Si no hay búsqueda, cargar todos los proveedores
      loadProveedores();
      return;
    }

    const request = ++requestId.current;
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await fetchAllSuppliers(query);
      if (request === requestId.current) setProveedores(data.map(transformProveedorFromAPI));
    } catch (err) {
      setError('Error de conexión en la búsqueda');
      console.error('Error searching proveedores:', err);
      setProveedores([]);
    } finally {
      setIsLoading(false);
    }
  }, [loadProveedores, transformProveedorFromAPI, fetchAllSuppliers]);

  /**
   * Eliminar proveedor
   */
  const deleteProveedor = useCallback(async (proveedorId) => {
    try {
      const result = await proveedoresService.deleteProveedor(proveedorId);
      
      if (result.success) {
        // Actualizar la lista local
        const updatedProveedores = proveedores.filter(p => p.id !== proveedorId);
        setProveedores(updatedProveedores);
        
        return {
          success: true,
          message: 'Proveedor eliminado exitosamente'
        };
      } else {
        return {
          success: false,
          error: result.message || 'Error al eliminar el proveedor'
        };
      }
    } catch (err) {
      console.error('Error deleting proveedor:', err);
      return {
        success: false,
        error: 'Error inesperado al eliminar el proveedor'
      };
    }
  }, [proveedores]);

  /**
   * Obtener estadísticas de proveedores
   */
  const getProveedoresStats = useCallback(() => {
    const stats = {
      total: proveedores.length,
      activos: proveedores.filter(p => p.status === 'activo').length,
      conProductos: proveedores.filter(p => p.productosActivos > 0).length,
      totalCompras: proveedores.reduce((sum, p) => sum + (p.totalCompras || 0), 0),
      totalOrdenes: proveedores.reduce((sum, p) => sum + (p.ordenesCompra || 0), 0)
    };
    return stats;
  }, [proveedores]);

  /**
   * Limpiar errores
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cargar proveedores al montar el componente
  useEffect(() => {
    loadProveedores();
  }, [loadProveedores]);

  // Efecto para búsqueda con debounce
  useEffect(() => {
    performSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery, performSearch]);

  return {
    proveedores,
    isLoading,
    error,
    searchQuery,
    loadProveedores,
    deleteProveedor,
    searchProveedores,
    getProveedoresStats,
    clearError
  };
};

export default useProveedores;